import { ErrorHandler, Injectable, Injector, LOCALE_ID, NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';

import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import {
  PERFECT_SCROLLBAR_CONFIG,
  PerfectScrollbarConfigInterface,
  PerfectScrollbarModule
} from 'ngx-perfect-scrollbar';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { ErrorHandlerService } from 'app/shared/services/error-handler.service';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { rootRouterConfig } from 'bookraw/app.routing';
import { AppComponent as BookrawAppComponent } from 'bookraw/app.component';
import { BookrawSharedModule } from 'bookraw/shared/shared.module';
import { UserService } from 'bookraw/shared/services/user.service';
import { CustomHttpInterceptor } from 'bookraw/shared/interceptor/interceptor';
import { QuillModule } from 'ngx-quill';
import { SchoolsService } from 'bookraw/shared/services/schools.service';
import { BooksService } from 'bookraw/shared/services/books.service';
import { UsersService } from 'bookraw/shared/services/users.service';
import { AvatarsService } from 'bookraw/shared/services/avatars.service';
import { ShelvesService } from 'bookraw/shared/services/shelves.service';
import { BadgesService } from 'bookraw/shared/services/badges.service';
import { TaxonomiesService } from 'bookraw/shared/services/taxonomies.service';
import { TaxonomyTermsService } from 'bookraw/shared/services/taxonomy-terms.service';
import { StaticUiTextsService } from 'bookraw/shared/services/static-ui-texts.service';
import { FaqsService } from 'bookraw/shared/services/faqs.service';
import { ConfigsService } from 'bookraw/shared/services/configs.service';
import { ToolbarService } from 'bookraw/shared/services/toolbar.service';
import { SchoolClassesService } from 'bookraw/shared/services/school-classes.service';
import { SchoolPackagesService } from 'bookraw/shared/services/school-packages.service';
import { StatisticsService } from 'bookraw/shared/services/statistics.service';
import { LicenseService } from 'bookraw/shared/services/license.service';
import { ModelEditorService } from 'bookraw/shared/services/model-editor.service';
import { SubscriptionPackagesService } from 'bookraw/shared/services/subscription-packages.service';
import { PartnersService } from 'bookraw/shared/services/partners.service';
import { CountriesService } from 'bookraw/shared/services/countries.service';
import { ClassroomService } from 'bookraw/shared/services/classroom.service';
import { SsoSettingsService } from 'bookraw/shared/services/sso-settings.service';
import { UserTestsService } from 'bookraw/shared/services/user-tests.service';
import Swal, { SweetAlertOptions } from 'sweetalert2';
import { TestsService } from 'bookraw/shared/services/tests.service';
import { QuestionsService } from 'bookraw/shared/services/questions.service';
import { BookSeriesService } from 'bookraw/shared/services/book-series.service';
import { PlaylistsService } from 'bookraw/shared/services/playlists.service';
import { CountryPresetsService } from 'bookraw/shared/services/country-presets.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'env/environment';
import localeDe from '@angular/common/locales/de';
import { registerLocaleData } from '@angular/common';
import { UserBookStatisticsService } from 'bookraw/shared/services/user-book-statistics';
import { LevelQuestionsService } from 'bookraw/shared/services/level-questions.service';
import { UserPlacementTestsService } from 'bookraw/shared/services/user-placement-tests.service';
import { BookAssignsService } from 'bookraw/shared/services/book-assigns.service';
import { LibrariesService } from 'bookraw/shared/services/libraries.service';

// Német locale regisztrálása, ha szükséges
if ( environment.locale === 'de' )  registerLocaleData(localeDe);

// AoT requires an exported function for factories
export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient);
}

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true
};

@Injectable()
export class ApiTranslateLoader implements TranslateLoader {
  constructor(private api: StaticUiTextsService) {
  }

  getTranslation(locale: string): Observable<any> {
    // apiból lekérdezzük a megfelelő nyelvi fájlt
    return this.api.getTexts( locale ).pipe(map((response) => response.result));
  }
}

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    BookrawSharedModule,
    HttpClientModule,
    PerfectScrollbarModule,
    QuillModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: ApiTranslateLoader,
        deps: [ StaticUiTextsService ]
      },
    }),
    RouterModule.forRoot(rootRouterConfig, { useHash: false })
  ],
  declarations: [BookrawAppComponent],
  providers: [
    ToolbarService,
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    { provide: PERFECT_SCROLLBAR_CONFIG, useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG },
    { provide: HTTP_INTERCEPTORS, useClass: CustomHttpInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: environment.locale },
    ModelEditorService,
    UserService,
    SchoolsService,
    BooksService,
    BookSeriesService,
    PlaylistsService,
    ClassroomService,
    UsersService,
    UserTestsService,
    UserPlacementTestsService,
    AvatarsService,
    ShelvesService,
    BadgesService,
    TaxonomiesService,
    TaxonomyTermsService,
    StaticUiTextsService,
    FaqsService,
    ConfigsService,
    SchoolClassesService,
    SchoolPackagesService,
    PartnersService,
    CountriesService,
    StatisticsService,
    LicenseService,
    SubscriptionPackagesService,
    SsoSettingsService,
    TestsService,
    QuestionsService,
    LibrariesService,
    CountryPresetsService,
    UserBookStatisticsService,
    LevelQuestionsService,
    BookAssignsService,
  ],
  bootstrap: [BookrawAppComponent]
})
export class BookrawAppModule {
  // az öröklődések miatt néhol egyszerűbb elérni statikusan mint átadni mindent
  // a parentnek ezért elérhető statikusan
  public static injector: Injector;

  public static showErrorMessage(message: string, ) {
    Swal.fire({
      titleText: this.injector.get(TranslateService).instant('general.error'),
      text: message,
      icon: 'error',
      timer: 5000,
      showCloseButton: true,
      showConfirmButton: false
    });
  }

  public static showSuccessMessage(message: string) {
    Swal.fire({
      titleText: this.injector.get(TranslateService).instant('general.success'),
      text: message,
      icon: 'success',
      timer: 3000,
      showCloseButton: true,
      showConfirmButton: false
    });
  }

  public static confirmMessage(message: string, callback: () => void = null) {
    Swal.fire({
      title: message,
      showDenyButton: true,
      showCancelButton: false,
      confirmButtonText: 'OK',
      denyButtonText: this.injector.get(TranslateService).instant('general.cancel'),
    }).then((result) => {
      if (result.isConfirmed) callback();
    });
  }

  public static showMessage(options: SweetAlertOptions) {
    options.timer ??= 3000;
    options.showCloseButton ??= true;
    options.showConfirmButton ??= false;

    Swal.fire(options);
  }

  constructor(injector: Injector) {
    BookrawAppModule.injector = injector;
  }
}
